import { useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  GrowthBook,
  prefetchPayload,
  useGrowthBook,
} from '@growthbook/growthbook-react'
import Clarity from '@microsoft/clarity'
import { APP_NAME } from 'app-config'

import { setGrowthbookReadyAction } from 'root-redux/actions/common'
import {
  selectCurrentVariantCohort,
  selectGrowthBookExperiments,
  selectLanguage,
} from 'root-redux/selects/common'
import {
  selectUUID,
  selectUserCountryCode,
  selectUserGeoState,
} from 'root-redux/selects/user'
import { TAppDispatch } from 'root-redux/store/store'

import { captureFunnelError } from 'helpers/captureFunnelError'
import { FunnelErrorCode } from 'helpers/funnelErrors'
import { getAdNameFromSearch } from 'helpers/getAdNameFromSearch'
import { getMobileOperatingSystem } from 'helpers/getMobileOperatingSystem'

import { eventLogger } from 'services/eventLogger.service'

import { CLARITY_PROJECT_IDS } from 'analytics-constants'

const GROWTHBOOK_API_HOST = 'https://cdn.growthbook.io'

export const growthBookInstance = new GrowthBook({
  enableDevMode: true,
  apiHost: GROWTHBOOK_API_HOST,
  clientKey: process.env.GROWTHBOOK_CLIENT_KEY,
  subscribeToChanges: true,
})

export const prefetchGrowthBookPayload = (): void => {
  const clientKey = process.env.GROWTHBOOK_CLIENT_KEY

  if (!clientKey) return

  prefetchPayload({
    apiHost: GROWTHBOOK_API_HOST,
    clientKey,
    streaming: true,
  })
}

export const useGrowthBookInitialization = (): void => {
  const dispatch = useDispatch<TAppDispatch>()

  const { pathname, search } = useLocation()

  const growthBook = useGrowthBook()
  const isGrowthBookReady = growthBook.ready
  const uuid = useSelector(selectUUID)
  const cohort = useSelector(selectCurrentVariantCohort)
  const language = useSelector(selectLanguage)
  const userCountryCode = useSelector(selectUserCountryCode)
  const experimentsMap = useSelector(selectGrowthBookExperiments)
  const userStateCode = useSelector(selectUserGeoState)

  const adName = useMemo(() => getAdNameFromSearch(window.location.search), [])
  const mobileOperatingSystem = useMemo(() => getMobileOperatingSystem(), [])

  useEffect(() => {
    growthBookInstance.init({ streaming: true }).catch((error) => {
      captureFunnelError(FunnelErrorCode.FUNNEL_GROWTHBOOK_INIT_FAILED, error)
    })
  }, [uuid])

  useEffect(() => {
    growthBookInstance.setAttributes({
      id: uuid,
      cohort,
      language,
      country: userCountryCode,
      stateCode: userStateCode,
      domain: window.location.hostname,
      url: `${pathname}${search}`,
      mobileOperatingSystem,
      adName,
    })

    dispatch(
      setGrowthbookReadyAction(
        isGrowthBookReady && !!uuid && !!cohort && !!language,
      ),
    )
  }, [
    adName,
    cohort,
    dispatch,
    isGrowthBookReady,
    language,
    mobileOperatingSystem,
    pathname,
    search,
    userCountryCode,
    userStateCode,
    uuid,
  ])

  useEffect(() => {
    const unsubscribe = growthBookInstance.subscribe((experiment, result) => {
      if (!experimentsMap || !result.hashUsed) return

      const experimentKey = experiment.key || ''
      const variantId = String(result.variationId)
      const variantName = experimentsMap?.[experimentKey]?.[variantId]
      const clarityProjectKey = CLARITY_PROJECT_IDS[APP_NAME]

      if (clarityProjectKey && variantName) {
        Clarity?.setTag('growthbook_segment', variantName)
      }

      eventLogger.logGrowthBookAbSegmentName({
        variantId,
        experimentKey,
        variantName,
      })
    })

    return () => unsubscribe()
  }, [experimentsMap])
}
